import instance from "@store/middleware/api";

import { hasTrackingConsent } from "./consent";

/**
 * First-party analytics beacons. Fire-and-forget — failures are swallowed so
 * analytics never interrupts the UX. Mirrors `utils/recordMediaEvent.ts`.
 *
 * CONSENT — the gate is on the DEVICE and on the IDENTIFIER, not on the beacon.
 * Two laws, and they bite in different places:
 *  - ePrivacy (TKG §165(3)) covers writing to the device. So `as_attr`
 *    (sessionStorage) and `as_sid` (a cookie the SERVER mints) wait for consent.
 *  - GDPR covers what we store. An event with no identifier isn't personal data
 *    (Recital 26) — so page views and searches are still SENT when the visitor
 *    declines, and the backend records them anonymously.
 *
 * Which is why these fire unconditionally: sending an HTTP request stores nothing
 * on anyone's device. What's withheld is the attribution and the identity — the
 * backend independently refuses to mint `as_sid` without the consent cookie and
 * strips referrer/UTM/userId from anonymous rows, so a missed gate here can't
 * start tracking anyone.
 *
 * - The device session id is a SERVER-issued HttpOnly cookie (`as_sid`), sent
 *   automatically with every beacon (the axios instance uses withCredentials) —
 *   the client never generates or sends it, so it can't be forged. Shared with
 *   media-engagement events so a device's behaviour groups under one id.
 * - First-touch attribution (referrer + UTM) is captured once per browser
 *   session and sent with every page view; the backend only keeps it on the
 *   session row's first sighting.
 *
 * See docu/flows/analytics/first-party-tracking.md.
 */

const ATTR_KEY = "as_attr";
const IDENTIFIED_KEY = "as_identified";

interface Attribution {
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

/**
 * Our own pages are not a traffic source. Without this, a visitor who accepts
 * consent on their SECOND page would have attribution captured right then with
 * `document.referrer` pointing at their first page — recording audiosanctum.com
 * as its own referrer and polluting Traffic Sources with self-referrals.
 */
const externalReferrer = (): string | undefined => {
  const raw = document.referrer;
  if (!raw) return undefined;
  try {
    if (new URL(raw).host === window.location.host) return undefined;
  } catch {
    return undefined;
  }
  return raw.slice(0, 512);
};

/**
 * Read the entry referrer/UTM at module load and hold it in memory ONLY.
 *
 * Nothing is written to the device and nothing is transmitted here — that all
 * waits for consent. The capture has to happen this early because the signal is
 * perishable: by the time a visitor reads the banner and clicks Accept on page
 * two, `document.referrer` is our own page one and the real source is gone
 * forever. Holding it in a JS variable that is discarded if consent never
 * arrives keeps the *storage* and the *sending* on the right side of the gate
 * while preserving the data for visitors who do say yes.
 */
const entryAttribution: Attribution = (() => {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      referrer: externalReferrer(),
      utmSource: params.get("utm_source") || undefined,
      utmMedium: params.get("utm_medium") || undefined,
      utmCampaign: params.get("utm_campaign") || undefined,
    };
  } catch {
    return {};
  }
})();

/** First-touch attribution for this browser session. Only persisted — and only
    sent — once the visitor has accepted; sessionStorage is device storage. */
const getAttribution = (): Attribution => {
  if (!hasTrackingConsent()) return {};
  try {
    const cached = sessionStorage.getItem(ATTR_KEY);
    if (cached) return JSON.parse(cached);
    sessionStorage.setItem(ATTR_KEY, JSON.stringify(entryAttribution));
    return entryAttribution;
  } catch {
    return entryAttribution;
  }
};

/** Always sent. With consent the backend ties it to the device session and keeps
    the attribution; without, it lands as an anonymous "this page was viewed". */
export const trackPageView = (path: string): void => {
  try {
    instance.post("/analytics/pageview", { path, ...getAttribution() }).catch(() => {});
  } catch {
    /* swallow */
  }
};

/** Always sent — search demand and zero-result terms are aggregate signals the
    backend records anonymously when the visitor has declined. */
export const trackSearch = (query: string, resultCount?: number): void => {
  const q = (query || "").trim();
  if (!q) return;
  try {
    instance.post("/analytics/search", { query: q.slice(0, 256), resultCount }).catch(() => {});
  } catch {
    /* swallow */
  }
};

const ACTIVE_SEARCH_KEY = "as_active_search";

/** Remember the query whose results are currently on screen, so a result
    click (track played/opened) can be attributed back to its search. */
/**
 * Held in memory always, mirrored to sessionStorage only with consent.
 *
 * The in-memory copy is what lets us attribute a result click for a declining
 * visitor: search and click happen on the same catalog page, so a variable covers
 * it, and a variable is not storage on their device. sessionStorage only buys the
 * cross-navigation case, so it's the part that waits for consent.
 */
let activeSearchInMemory: string | null = null;

export const setActiveSearch = (query: string | null): void => {
  const q = query?.trim() || null;
  activeSearchInMemory = q;
  try {
    // Removal always runs — clearing stale state must not depend on consent.
    if (!q) {
      sessionStorage.removeItem(ACTIVE_SEARCH_KEY);
      return;
    }
    if (!hasTrackingConsent()) return;
    sessionStorage.setItem(ACTIVE_SEARCH_KEY, q);
  } catch {
    /* swallow */
  }
};

export const getActiveSearch = (): string | null => {
  if (activeSearchInMemory) return activeSearchInMemory;
  try {
    return sessionStorage.getItem(ACTIVE_SEARCH_KEY);
  } catch {
    return null;
  }
};

/** Fire when a visitor engages with a search result (plays/opens a track while a
    search is active). No-op when no search is active. */
export const trackSearchClick = (trackId: string | undefined, position?: number): void => {
  const query = getActiveSearch();
  if (!query || !trackId) return;
  try {
    instance.post("/analytics/search-click", { query: query.slice(0, 256), trackId, position }).catch(() => {});
  } catch {
    /* swallow */
  }
};

/**
 * Report a cookie-consent decision so we can demonstrate it later (GDPR Art 7(1)).
 *
 * Sent for a REFUSAL too — that's the record we'd most need to produce, and
 * keeping it only for people who said yes would defeat the point.
 *
 * Sends ONLY `source`. The consentId, categories, revision and timestamps are
 * read server-side from the `as_consent` cookie, which the request carries
 * anyway: a body-supplied consentId would let a logged-in caller claim someone
 * else's consent onto their account (attribution hijack, S-061). Nothing else
 * belongs in this payload. The backend dedupes, so firing on every page load is
 * harmless.
 */
export const reportConsent = (source: "banner" | "preferences"): void => {
  try {
    instance.post("/analytics/consent", { source }).catch(() => {});
  } catch {
    /* swallow */
  }
};

/**
 * Undo tracking when the visitor withdraws consent in the preferences centre.
 *
 * Withdrawal has to be as effective as consent, and none of this happens on its
 * own: the beacons stop by themselves (they re-check on every call), but the
 * state already on the device does not. `as_sid` in particular is HttpOnly and a
 * year long, so only the server can remove it — hence the explicit call rather
 * than waiting for the next request that happens to touch it.
 */
export const forgetTracking = (): void => {
  try {
    sessionStorage.removeItem(ATTR_KEY);
    sessionStorage.removeItem(ACTIVE_SEARCH_KEY);
    sessionStorage.removeItem(IDENTIFIED_KEY);
  } catch {
    /* swallow */
  }
  try {
    instance.post("/analytics/forget", {}).catch(() => {});
  } catch {
    /* swallow */
  }
};

/**
 * Link the current device session to the (now authenticated) user — once per
 * browser session. The "already done" flag is tracking storage and lives behind
 * the same gate as the call it guards: writing it while the beacon is gated
 * would both store a tracking key without consent AND poison the flag, so a
 * visitor who accepted later would never be identified at all.
 */
export const identifySession = (): void => {
  if (!hasTrackingConsent()) return;
  try {
    if (sessionStorage.getItem(IDENTIFIED_KEY)) return;
    sessionStorage.setItem(IDENTIFIED_KEY, "1");
  } catch {
    /* storage unavailable — fall through and identify anyway */
  }
  try {
    instance.post("/analytics/identify", {}).catch(() => {});
  } catch {
    /* swallow */
  }
};
